import {
  ActionHandler,
  ActionRegistry,
  ValidationResult,
} from '@/core/ambient-intelligence/components/registries/ActionRegistry';
import { ActionSpec, ContentItem } from '@/core/ambient-intelligence/types/ambient-intelligence.types';
import { metadataCache } from '@/core/ambient-intelligence/utils/MetadataCache';
import { getMetaData } from '@/core/apis/bentoboard-apis';
import { appendImageRepoDomainIfNotExistInUrl } from '@/core/utils/helpers';

// The context isn't used for now but kept for future extensions
type ActionContext = Record<string, never>;

class OpenContentActionHandler implements ActionHandler {
  canHandle(action: ActionSpec): boolean {
    return action.type === 'openProduct' || action.type === 'openStory';
  }

  validate(params: any): ValidationResult {
    if (!params?.href) {
      return { valid: false, error: 'Missing href for open action' };
    }
    return { valid: true };
  }

  async execute(action: ActionSpec, _context: ActionContext) {
    const href: string = action.params?.href;
    const url = href.startsWith('http') ? href : `https://spaces.qa.bldr.it${href}`;
    const cacheKey = `metadata_${url}`;

    try {
      let meta = metadataCache.get(cacheKey);
      if (!meta) {
        meta = await getMetaData(null, url);
        if (meta) {
          metadataCache.set(cacheKey, meta);
        }
      }

      if (!meta) {
        return { success: false, error: 'Metadata not found' };
      }

      const content = this.transformMetadata(meta, action.type, href);
      return {
        success: true,
        result: {
          type: 'create_content',
          data: { content },
        },
      };
    } catch (err: any) {
      return { success: false, error: err.message || 'Failed to fetch metadata' };
    }
  }

  private transformMetadata(meta: any, type: string, href: string): ContentItem {
    if (type === 'openProduct') {
      return {
        id: meta.productId || meta.boardId || meta.id || `product_${Date.now()}`,
        type: 'product',
        title: meta.name || meta.title || 'Product',
        description: meta.description || '',
        media: meta.previewImage
          ? { thumbnail: { url: appendImageRepoDomainIfNotExistInUrl(meta.previewImage) } }
          : {},
        tags: [],
        categories: ['Products'],
        engagement: {},
        temporal: { createdAt: new Date().toISOString() },
        source: { origin: 'business' },
        data: {
          productUrl: meta.url || meta.productUrl || href,
          isRealProduct: true,
          isFromWebsite: true,
          vendorId: meta.vendorId,
        },
      };
    }

    // openStory
    return {
      id: meta.boardId || meta.id || `blog_${Date.now()}`,
      type: 'blog',
      title: meta.name || meta.title || 'Blog',
      description: meta.description || '',
      media: meta.displayImage
        ? {
            hero: {
              url: appendImageRepoDomainIfNotExistInUrl(meta.displayImage),
              alt: meta.name || meta.title,
            },
          }
        : {},
      tags: meta.tags || [],
      categories: ['Blog'],
      engagement: {},
      temporal: { createdAt: meta.createdAt || new Date().toISOString() },
      source: { origin: 'business' },
      data: {
        blogUrl: meta.url || href,
        isFromWebsite: true,
        needsMetadata: false,
        boardId: meta.boardId || meta.id,
        accessType: meta.accessType,
        isMember: meta.isMember,
        isExclusive: meta.isExclusive,
        generatedSummary: meta.summary,
        questions: meta.questions,
      },
    };
  }
}

const handler = new OpenContentActionHandler();
ActionRegistry.register('openProduct', handler);
ActionRegistry.register('openStory', handler);

export { OpenContentActionHandler };
